import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldOff, Sun, Moon, Monitor } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { cn, PageLoading } from "@/components/common";
import { LocationSettings } from "@/components/community";

const THEME_OPTIONS = [
  { id: "light", label: "Light", icon: Sun },
  { id: "dark", label: "Dark", icon: Moon },
  { id: "system", label: "Use device setting", icon: Monitor },
];

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const [form, setForm] = useState({ name: "", bio: "", suburb: "", privacy: { show_suburb: true }, notif_prefs: { messages: true, marketing: false } });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [history, setHistory] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const loadBlocks = () => api.get("/blocks").then(({ data }) => setBlocks(data.items)).catch(() => {});
  useEffect(() => {
    if (user) setForm({ name: user.name || "", bio: user.bio || "", suburb: user.suburb || "", privacy: user.privacy || { show_suburb: true }, notif_prefs: user.notif_prefs || { messages: true, marketing: false } });
    api.get("/payments/history").then(({ data }) => setHistory(data.items)).catch(() => {});
    loadBlocks();
  }, [user]);
  if (!user) return <PageLoading />;
  const unblock = async (uid) => { if (!window.confirm("Unblock this user? They'll be able to message and see your listings again.")) return; try { await api.delete(`/blocks/${uid}`); toast.success("User unblocked"); loadBlocks(); } catch (e) { toast.error(apiError(e)); } };
  const save = async () => { try { await api.put("/auth/profile", form); await refreshUser(); toast.success("Profile updated"); } catch (e) { toast.error(apiError(e)); } };
  const changePw = async () => { try { await api.post("/auth/change-password", pw); toast.success("Password changed"); setPw({ current_password: "", new_password: "" }); } catch (e) { toast.error(apiError(e)); } };
  const deactivate = async () => { if (!window.confirm("Deactivate your account?")) return; try { await api.post("/auth/deactivate"); toast.success("Account deactivated"); } catch (e) { toast.error(apiError(e)); } };
  const chooseTheme = (t) => { setTheme(t); api.put("/auth/profile", { theme_preference: t }).catch(() => toast.error("Theme saved on this device, but syncing to your account failed.")); };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <h1 className="font-display text-2xl font-bold tracking-tight">Settings</h1>
      <section className="space-y-3 rounded-2xl border border-border bg-card p-5" data-testid="appearance-section">
        <div>
          <h2 className="font-display font-semibold">Appearance</h2>
          <p className="text-sm text-muted-foreground">Choose how Mtaa looks on your device.</p>
        </div>
        <div className="overflow-hidden rounded-xl border border-border">
          {THEME_OPTIONS.map((o, i) => { const Icon = o.icon; const activeSel = theme === o.id; return (
            <button key={o.id} onClick={() => chooseTheme(o.id)} data-testid={`theme-${o.id}`}
              className={cn("flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors", i > 0 && "border-t border-border", "hover:bg-secondary/50")}>
              <Icon className="h-5 w-5 text-muted-foreground" />
              <span className="flex-1 font-medium text-foreground">{o.label}</span>
              <span className={cn("flex h-5 w-5 items-center justify-center rounded-full border-2", activeSel ? "border-primary" : "border-border")}>
                {activeSel && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
              </span>
            </button>
          ); })}
        </div>
      </section>
      <LocationSettings />
      <section className="space-y-3 rounded-2xl border border-border bg-card p-5">
        <h2 className="font-display font-semibold">Profile</h2>
        <div><Label>Display name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="set-name" /></div>
        <div><Label>Bio</Label><Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="set-bio" /></div>
        <div><Label>Suburb</Label><Input value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })} data-testid="set-suburb" /></div>
        <label className="flex items-center justify-between text-sm"><span>Show my suburb publicly</span><Switch checked={form.privacy.show_suburb} onCheckedChange={(v) => setForm({ ...form, privacy: { ...form.privacy, show_suburb: v } })} data-testid="set-show-suburb" /></label>
        <label className="flex items-center justify-between text-sm"><span>Marketing emails</span><Switch checked={form.notif_prefs.marketing} onCheckedChange={(v) => setForm({ ...form, notif_prefs: { ...form.notif_prefs, marketing: v } })} data-testid="set-marketing" /></label>
        <Button onClick={save} data-testid="set-save">Save changes</Button>
      </section>
      <section className="space-y-3 rounded-2xl border border-border bg-card p-5">
        <h2 className="font-display font-semibold">Change password</h2>
        <Input type="password" placeholder="Current password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="set-current-pw" />
        <Input type="password" placeholder="New password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="set-new-pw" />
        <Button variant="outline" onClick={changePw} data-testid="set-change-pw">Change password</Button>
      </section>
      <section className="flex items-center justify-between gap-3 rounded-2xl border border-brand-green/30 bg-brand-green/5 p-5" data-testid="support-mtaa-section">
        <div>
          <h2 className="font-display font-semibold">Support Mtaa ❤️</h2>
          <p className="text-sm text-muted-foreground">Make an optional contribution towards hosting, development and new features. Completely optional.</p>
        </div>
        <Link to="/support"><Button variant="outline" data-testid="settings-support-link">Support Mtaa</Button></Link>
      </section>
      <section className="space-y-2 rounded-2xl border border-border bg-card p-5">
        <h2 className="font-display font-semibold">Billing history</h2>
        {history.length === 0 ? <p className="text-sm text-muted-foreground">No transactions yet.</p> : history.map((h) => (
          <div key={h.session_id} className="flex items-center justify-between border-b border-border py-2 text-sm last:border-0">
            <span>{h.lookup_key?.replace(/_/g, " ")}</span>
            <span className="font-medium">${(h.amount / 100).toFixed(2)} · {h.payment_status}</span>
          </div>
        ))}
      </section>
      <section className="space-y-2 rounded-2xl border border-border bg-card p-5" data-testid="privacy-safety">
        <h2 className="font-display font-semibold">Privacy &amp; Safety — Blocked users</h2>
        {blocks.length === 0 ? <p className="text-sm text-muted-foreground">You haven't blocked anyone.</p> : blocks.map((b) => (
          <div key={b.user.id} className="flex items-center justify-between border-b border-border py-2 last:border-0" data-testid={`blocked-user-${b.user.id}`}>
            <Link to={`/u/${b.user.id}`} className="flex items-center gap-2 hover:underline">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary font-semibold text-primary">{(b.user.name || "U")[0]}</div>
              <div><p className="text-sm font-medium">{b.user.name}</p><p className="text-xs text-muted-foreground">Blocked {new Date(b.created_at).toLocaleDateString("en-AU")}</p></div>
            </Link>
            <Button size="sm" variant="outline" onClick={() => unblock(b.user.id)} data-testid={`unblock-${b.user.id}`}><ShieldOff className="h-4 w-4" />Unblock</Button>
          </div>
        ))}
      </section>
      <section className="rounded-2xl border border-destructive/30 bg-destructive/5 p-5">
        <h2 className="font-display font-semibold text-destructive">Danger zone</h2>
        <p className="text-sm text-muted-foreground">Deactivate your account. You can request full data deletion by contacting support.</p>
        <Button variant="destructive" className="mt-2" onClick={deactivate} data-testid="set-deactivate">Deactivate account</Button>
      </section>
    </div>
  );
}
